import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { OfferBuilderVariant, WorkingTakerMarket, RateType, OfferBuilderPricingStrategy } from './OfferBuilderContext';
import { DraftRateDetails } from './useDraftRateDetails';
import { buildTakerMarketKey } from './utils';
import type { FixedRateDetails } from '@/enterprise/features/fixedRate/useFixedRateDetails';
import type { PreferredRateDetails } from '@/enterprise/features/preferred/usePreferredRateDetails';
import type { VariableRateDetails } from '@/enterprise/features/variableRate/useVariableRateDetails';

export interface RateHelpers {
  getFixedRate: (takerMarket: TakerMarket) => FixedRateDetails;
  getVariableRate: (takerMarket: TakerMarket | null) => VariableRateDetails;
  getPreferredRate: (takerMarket: TakerMarket | null) => PreferredRateDetails;
  getDraftRate: (takerMarket: TakerMarket) => DraftRateDetails;
}

export interface EffectiveRateOptions {
  workingTakerMarkets: Record<string, WorkingTakerMarket>;
  builderVariant: OfferBuilderVariant;
  rateHelpers: RateHelpers;
}

export interface EffectiveRateResult {
  rate: number | undefined;
  rateType: 'APR' | 'DISC' | undefined;
  pricingStrategy?: OfferBuilderPricingStrategy;
}

export interface RateInfo {
  rate: number;
  rateType: RateType;
  pricingRateUuid?: string;
  suggestedRate?: number;
  suggestRateType?: RateType;
  customRate?: number;
  customRateType?: RateType;
  pricingStrategy?: OfferBuilderPricingStrategy;
  isIndividuallyOverridden?: boolean;
  projectedClearingPercentage?: number;
}

export type RateMap = Record<string, RateInfo | undefined>;

export const getEffectiveRateFromData = (
  takerMarket: TakerMarket,
  options: EffectiveRateOptions
): EffectiveRateResult => {
  const { builderVariant, rateHelpers, workingTakerMarkets } = options;

  if (builderVariant === 'draft') {
    const { rate, rateType } = rateHelpers.getDraftRate(takerMarket);
    return { rate, rateType };
  } else if (takerMarket.offerConfig.offerType === 'PREFERRED_TERM') {
    const { estimatedRate, rateType: preferredRateType } = rateHelpers.getPreferredRate(takerMarket);
    return { rate: estimatedRate, rateType: preferredRateType };
  } else if (takerMarket.marketPricingType === 'PRICE_DISCOVERY') {
    const takerMarketKey = buildTakerMarketKey(takerMarket);
    const workingMarket = workingTakerMarkets[takerMarketKey];
    return {
      rate: workingMarket?.rate ?? undefined,
      rateType: workingMarket?.rateType ?? undefined,
      pricingStrategy: workingMarket?.pricingStrategy,
    };
  } else if (takerMarket.marketPricingType === 'STATIC') {
    const { marketRate } = rateHelpers.getFixedRate(takerMarket);
    return { rate: marketRate ?? undefined, rateType: 'APR' };
  } else if (takerMarket.marketPricingType === 'BENCHMARK') {
    const { estimatedRate } = rateHelpers.getVariableRate(takerMarket);
    return { rate: estimatedRate, rateType: 'APR' };
  }

  return { rate: undefined, rateType: undefined };
};

export const getRateRangeFromData = (
  selected: TakerMarket[],
  getEffectiveRateFn: (tm: TakerMarket) => EffectiveRateResult,
  makerOrgUuid?: string
) => {
  const filteredSelected = makerOrgUuid ? selected.filter((tm) => tm.makerOrganizationUuid === makerOrgUuid) : selected;

  const ratesInfo = filteredSelected.map(getEffectiveRateFn);

  if (ratesInfo.some((rateInfo) => rateInfo.rate === undefined)) {
    return {
      minAprRate: undefined,
      maxAprRate: undefined,
      minDiscRate: undefined,
      maxDiscRate: undefined,
      isMixedRateTypes: false,
      isRateRange: false,
    };
  }

  const getRateRange = (rateType: 'APR' | 'DISC') => {
    const rates = ratesInfo
      .filter((rateInfo) => rateInfo.rateType === rateType)
      .map((rateInfo) => rateInfo.rate!)
      .filter((rate) => rate !== undefined);

    return rates.length > 0 ? { min: Math.min(...rates), max: Math.max(...rates) } : { min: undefined, max: undefined };
  };

  const aprRange = getRateRange('APR');
  const discRange = getRateRange('DISC');
  const isAprRateRange = aprRange.min !== aprRange.max;
  const isDiscRateRange = discRange.min !== discRange.max;

  return {
    minAprRate: aprRange.min,
    maxAprRate: aprRange.max,
    isAprRateRange,
    minDiscRate: discRange.min,
    maxDiscRate: discRange.max,
    isDiscRateRange,
    isMixedRateTypes: aprRange.min !== undefined && discRange.min !== undefined,
    isRateRange: isAprRateRange || isDiscRateRange,
  };
};

// original and working rates
export interface RateComparison {
  originalRate: number | undefined;
  originalRateType: 'APR' | 'DISC';
  workingRate: number | undefined;
  workingRateType: 'APR' | 'DISC' | undefined;
  hasWorkingState: boolean;
  hasRateChanged: boolean;
}

// taker market with its calculated effective rate
export interface TakerMarketWithRate extends EffectiveRateResult {
  takerMarket: TakerMarket;
}

// taker market with rate comparison
export interface TakerMarketWithRateComparison extends RateComparison {
  takerMarket: TakerMarket;
}

export const getRateComparisonFromData = (
  takerMarket: TakerMarket,
  getEffectiveRateFn: (tm: TakerMarket) => EffectiveRateResult,
  workingTakerMarkets: Record<string, WorkingTakerMarket>
): RateComparison => {
  const takerMarketKey = buildTakerMarketKey(takerMarket);
  const workingMarket = workingTakerMarkets[takerMarketKey];
  const hasWorkingState = workingMarket !== undefined;
  const { rate: workingRate, rateType: workingRateType } = getEffectiveRateFn(takerMarket);

  // get original rate
  const originalRate = takerMarket.offerConfig.isDiscountBidding
    ? takerMarket.offerConfig.maxDiscount ?? undefined
    : takerMarket.offerConfig.maxApr ?? undefined;
  const originalRateType = takerMarket.offerConfig.isDiscountBidding ? 'DISC' : 'APR';

  // determine if rate has changed
  const hasRateChanged =
    hasWorkingState &&
    workingRate !== undefined &&
    workingRateType !== undefined &&
    originalRate !== undefined &&
    (originalRate !== workingRate || originalRateType !== workingRateType);

  return {
    originalRate,
    originalRateType,
    workingRate,
    workingRateType,
    hasWorkingState,
    hasRateChanged,
  };
};

export const calculateDiscountFromAmount = ({
  dpeWeightedSum,
  invoiceAmount,
  rate,
}: {
  dpeWeightedSum: number;
  invoiceAmount: number;
  rate: number;
}) => {
  const weightedAverageDpe = invoiceAmount > 0 ? dpeWeightedSum / invoiceAmount : 0;
  const equivalentDisc = (rate / 100) * (weightedAverageDpe / 360);
  const estimatedDiscountAmount = invoiceAmount * equivalentDisc;
  return estimatedDiscountAmount;
};
